import { ReactNode, lazy, Suspense, useMemo } from 'react';
import { compose } from 'recompose';

import CustomBranding from '@mindtickle/custom-branding';

import GlobalHeaderLoader from '~/components/GlobalHeader/Learner/Loader';
import Navigation from '~/components/Navigation';
import Maintenance from '~/components/Maintenance';
import { ErrorBoundary } from '~/components/Tools';
import { useUserAuth } from '~/components/Auth';

import { StyledBerryWidget, StyledLearnerShellLayout } from './styles';
import { withTranslationsInternal } from '~/components/Intl';
import { BERRY_WIDGET_ID } from '~/config/app.constants';
import { useImpersonation } from '~/hooks/useImpersonation';
import ImpersonationBanner from '~/components/ImpersonationBanner';
import { isLayoutHidden, setDefaultBrandColor } from './utils';
import classNames from 'classnames';
import { withConditionalLayout } from '../hocs/withConditionalLayout';
import { withOtherRemotes } from '../hocs/withOtherRemotes';

const GlobalLearnerHeaderLazy = lazy(() => import('~/components/GlobalHeader/Learner'));

type LayoutProps = { children?: ReactNode };
const BaseLearnerLayout = ({ children }: LayoutProps) => {
  const {
    company: { companySettings: { isExperienceCloudEnabled = false, brandColor = '' } = {} } = {},
  } = useUserAuth();
  const isImpersonated = useImpersonation();
  const hideLayout = useMemo(() => isLayoutHidden(), []);
  return (
    <CustomBranding brandColor={setDefaultBrandColor(brandColor)}>
      <StyledLearnerShellLayout
        className={classNames({
          'is-impersonated': isImpersonated,
          'layout-hidden': hideLayout,
          'experience-cloud-enabled': isExperienceCloudEnabled,
        })}
      >
        <ErrorBoundary>
          <ImpersonationBanner />
        </ErrorBoundary>
        <ErrorBoundary>
          <Maintenance isAdmin={false} />
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<GlobalHeaderLoader />}>
            <GlobalLearnerHeaderLazy />
          </Suspense>
        </ErrorBoundary>
        <div className="shell-layout-learner-content">
          <ErrorBoundary>
            <Navigation />
          </ErrorBoundary>
          <div className="shell-layout-learner-content-wrapper">
            <StyledBerryWidget id={BERRY_WIDGET_ID} />
            <div className="shell-layout-content-area">{children}</div>
          </div>
        </div>
      </StyledLearnerShellLayout>
    </CustomBranding>
  );
};

/**
 * CRITICAL: DO NOT CHANGE THE ORDER OF THESE HOCs
 *
 * The composition order of withTranslationsInternal, withOtherRemotes, and withConditionalLayout is crucial
 * for preventing component unmounting issues that cause application refresh problems.
 *
 * Current order (MUST BE MAINTAINED):
 * 1. withTranslationsInternal - Provides translation context
 * 2. withOtherRemotes - Handles dynamic remote loading and deferred integrations
 * 3. withConditionalLayout - Handles layout visibility logic
 *
 * Why this order matters:
 * - RelayProvider and other context providers depend on stable component references
 * - Changing HOC order creates new component instances on every render
 * - This causes React to treat components as different types, triggering unmount/mount cycles
 * - The current order ensures component reference stability across re-renders
 */
const LayoutWithTranslations = compose(
  withTranslationsInternal({
    appName: 'ui-shell',
    loader: <GlobalHeaderLoader />,
  }),
  withOtherRemotes,
  withConditionalLayout
)(BaseLearnerLayout);

export default LayoutWithTranslations;
